import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Nav, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import {
  RIGGING_OPERATIONS,
  getClientRuntimeAvailability,
  getRiggingOperation,
  isAiRiggingJobCreationEnabled,
  operationLabel,
} from '../features/aiRigging';
import { shortHash } from '../features/riggingJobStatus';
import RiggingOperationCard from '../components/rigging/RiggingOperationCard';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';
import RiggingRuntimeRequirements from '../components/rigging/RiggingRuntimeRequirements';
import RiggingExecutionPackageSummary from '../components/rigging/RiggingExecutionPackageSummary';
import ProjectBreadcrumbs from '../components/ProjectBreadcrumbs';
import { hasTenantPermission, RIGGING_JOB_CREATE } from '../auth/permissions';
import { useTenantPermissions } from '../hooks/useTenantPermissions';

const STEPS = [
  { id: 'character', label: 'Character' },
  { id: 'memory', label: 'Rig Memory' },
  { id: 'source', label: 'Source' },
  { id: 'operation', label: 'Operation' },
  { id: 'settings', label: 'Settings' },
  { id: 'review', label: 'Review' },
];

const INITIAL = {
  characterId: '',
  memoryVersionId: '',
  revisionId: '',
  sourceScenePath: '',
  operation: 'maya_rig_build',
  exportProfileKey: '',
  displayName: '',
  producePreview: false,
  preserveIntermediateArtifacts: true,
};

function isValidRelativeMayaScene(path) {
  if (!path || typeof path !== 'string') return false;
  if (path.includes('\0')) return false;
  if (path.includes('..')) return false;
  if (path.startsWith('/') || /^[a-zA-Z]:[\\/]/.test(path) || path.startsWith('\\\\')) {
    return false;
  }
  return path.endsWith('.ma') || path.endsWith('.mb');
}

export default function NewProjectRiggingJobPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions, hasTenantPermission: can } = useTenantPermissions();
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState({
    ...INITIAL,
    characterId: searchParams.get('character') || '',
  });
  const [characters, setCharacters] = useState([]);
  const [versions, setVersions] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [projectName, setProjectName] = useState('Project');

  const canCreate =
    (can(RIGGING_JOB_CREATE) || hasTenantPermission(permissions, RIGGING_JOB_CREATE)) &&
    isAiRiggingJobCreationEnabled();
  const step = STEPS[stepIndex];

  const selectedCharacter = characters.find((c) => c.id === form.characterId);
  const selectedVersion = versions.find((v) => v.id === form.memoryVersionId);
  const selectedRevision = revisions.find((r) => r.id === form.revisionId);
  const selectedOperation = getRiggingOperation(form.operation);

  const scenes = useMemo(() => {
    const summary = selectedRevision?.maya_manifest_summary;
    const fromManifest = summary?.scenes || [];
    if (fromManifest.length) {
      return fromManifest
        .map((s) => (typeof s === 'string' ? { path: s } : s))
        .filter((s) => isValidRelativeMayaScene(s.path));
    }
    const paths = selectedRevision?.scene_discovery_summary?.paths || [];
    return paths
      .filter((p) => isValidRelativeMayaScene(p))
      .map((path) => ({ path }));
  }, [selectedRevision]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [chars, revs, avail, projectData] = await Promise.all([
          bakingApi.listRigCharacters(projectId),
          bakingApi.listRevisions(projectId),
          bakingApi.getRiggingRuntimeAvailability(projectId).catch(() => null),
          bakingApi.getProject(projectId).catch(() => null),
        ]);
        if (cancelled) return;
        setCharacters((chars || []).filter((c) => c.status !== 'archived'));
        setRevisions(
          (revs || []).filter(
            (r) => r.status === 'ready' && (r.source_type === 'archive' || r.source_type === 'perforce')
          )
        );
        setAvailability(avail);
        if (projectData?.name) setProjectName(projectData.name);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load job workflow');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!form.characterId) {
      setVersions([]);
      setForm((prev) => ({ ...prev, memoryVersionId: '' }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await bakingApi.listRigCharacterVersions(projectId, form.characterId);
        if (cancelled) return;
        const published = (data || []).filter((v) => v.lifecycle_status === 'published');
        setVersions(published);
        const active = characters.find((c) => c.id === form.characterId)?.active_memory_version;
        setForm((prev) => ({
          ...prev,
          memoryVersionId:
            published.some((v) => v.id === prev.memoryVersionId)
              ? prev.memoryVersionId
              : published.find((v) => v.id === active)?.id || published[0]?.id || '',
        }));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load Rig Memory versions');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, form.characterId, characters]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, sourceScenePath: '' }));
  }, [form.revisionId]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, exportProfileKey: '' }));
  }, [form.operation, form.memoryVersionId]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const validateStep = (index) => {
    const errors = {};
    if (index === 0 && !form.characterId) errors.characterId = 'Select a character.';
    if (index === 0 && selectedCharacter?.status === 'paused') {
      errors.characterId = 'Paused characters cannot start new jobs.';
    }
    if (index === 1 && !form.memoryVersionId) {
      errors.memoryVersionId = 'Select a published Rig Memory version.';
    }
    if (index === 2 && !form.revisionId) errors.revisionId = 'Select a source revision.';
    if (index === 2 && !isValidRelativeMayaScene(form.sourceScenePath)) {
      errors.sourceScenePath = 'Select a project-relative .ma or .mb scene.';
    }
    if (index === 3) {
      const op = getRiggingOperation(form.operation);
      if (!op) errors.operation = 'Select an operation.';
      else if (!op.availability().available) {
        errors.operation = op.availability().reason || 'Operation unavailable.';
      }
    }
    if (index === 4 && form.operation === 'maya_engine_export' && !form.exportProfileKey) {
      errors.exportProfileKey = 'Select an export profile.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(stepIndex)) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  useEffect(() => {
    if (step.id !== 'review') return undefined;
    if (!form.characterId || !form.memoryVersionId || !form.revisionId || !form.sourceScenePath) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const data = await bakingApi.previewRiggingJob(projectId, {
          character_id: form.characterId,
          rig_memory_version_id: form.memoryVersionId,
          source_revision_id: form.revisionId,
          source_scene_path: form.sourceScenePath,
          operation: form.operation,
          export_profile_key: form.exportProfileKey || '',
          execution_options: {
            display_name: form.displayName || undefined,
            produce_preview: form.producePreview,
            preserve_intermediate_artifacts: form.preserveIntermediateArtifacts,
          },
        });
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setError(err.message || 'Preview failed');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step.id, projectId, form]);

  const submit = async () => {
    if (!canCreate) return;
    if (!validateStep(5) && preview && !preview.valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await bakingApi.createRiggingJob(projectId, {
        character_id: form.characterId,
        rig_memory_version_id: form.memoryVersionId,
        source_revision_id: form.revisionId,
        source_scene_path: form.sourceScenePath,
        operation: form.operation,
        export_profile_key: form.exportProfileKey || '',
        execution_options: {
          display_name: form.displayName || undefined,
          produce_preview: form.producePreview,
          preserve_intermediate_artifacts: form.preserveIntermediateArtifacts,
        },
      });
      navigate(`/console/projects/${projectId}/rigging/jobs/${created.job_id}`);
    } catch (err) {
      const code = err.code || '';
      if (code.includes('character') || code === 'character_archived') setStepIndex(0);
      else if (code.includes('memory') || code.includes('policy')) setStepIndex(1);
      else if (code.includes('path') || code.includes('scene') || code.includes('revision')) {
        setStepIndex(2);
      } else if (code.includes('operation') || code.includes('autoskin') || code.includes('export')) {
        setStepIndex(3);
      }
      setError(err.message || 'Failed to create rigging job');
    } finally {
      setSubmitting(false);
    }
  };

  const exportProfiles = selectedVersion?.configuration?.export_profiles || {};
  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];

  if (loading) {
    return (
      <div className="py-4">
        <Spinner animation="border" size="sm" /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="new-rigging-job-page">
      <ProjectBreadcrumbs
        items={[
          { label: 'Projects', to: '/console/projects' },
          { label: projectName, to: `/console/projects/${projectId}` },
          { label: 'Rigging Jobs', to: `/console/projects/${projectId}/rigging/jobs` },
          { label: 'New Job' },
        ]}
      />
      <h1 className="product-page-title">New Rigging Job</h1>
      <p className="product-page-subtitle">
        The Rigging Bible is the published Project Rig Memory that defines how this character should
        be constructed, skinned, validated, and exported.
      </p>
      <RiggingRuntimeNotice availability={availability || getClientRuntimeAvailability()} />

      <Nav variant="pills" className="mb-3 flex-wrap gap-1">
        {STEPS.map((s, index) => (
          <Nav.Item key={s.id}>
            <Nav.Link
              active={index === stepIndex}
              onClick={() => {
                if (index < stepIndex) setStepIndex(index);
              }}
              disabled={index > stepIndex}
            >
              {index + 1}. {s.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {error ? <div className="error-state mb-3">{error}</div> : null}

      {step.id === 'character' ? (
        <div className="product-card" data-testid="step-character">
          <h3 className="h6">Select character</h3>
          <Form.Group className="mb-3">
            <Form.Label>Character</Form.Label>
            <Form.Select
              value={form.characterId}
              isInvalid={Boolean(fieldErrors.characterId)}
              onChange={(e) =>
                patch({
                  characterId: e.target.value,
                  memoryVersionId: '',
                })
              }
            >
              <option value="">Choose a character…</option>
              {characters.map((c) => (
                <option key={c.id} value={c.id} disabled={c.status === 'paused'}>
                  {c.display_name} ({c.slug}) · {c.character_type}
                  {c.status === 'paused' ? ' — paused' : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {fieldErrors.characterId}
            </Form.Control.Feedback>
          </Form.Group>
          {selectedCharacter ? (
            <dl className="row mb-0 small">
              <dt className="col-sm-3">Status</dt>
              <dd className="col-sm-9">{selectedCharacter.status}</dd>
              <dt className="col-sm-3">Active Rig Memory</dt>
              <dd className="col-sm-9 mono">{selectedCharacter.active_memory_version || '—'}</dd>
              <dt className="col-sm-3">Source scene hint</dt>
              <dd className="col-sm-9 mono">{selectedCharacter.source_scene_hint || '—'}</dd>
            </dl>
          ) : null}
          <p className="mt-3 mb-0">
            <Link to={`/console/projects/${projectId}/rigging/characters?returnTo=jobs/new`}>
              Create character
            </Link>
          </p>
        </div>
      ) : null}

      {step.id === 'memory' ? (
        <div className="product-card" data-testid="step-memory">
          <h3 className="h6">Select published Rig Memory</h3>
          <Alert variant="info" className="py-2">
            This job will permanently pin Rig Memory version{' '}
            {selectedVersion?.version_number || '…'}. Later edits to the Rigging Bible will not
            change this job.
          </Alert>
          <Form.Group>
            <Form.Label>Published version</Form.Label>
            <Form.Select
              value={form.memoryVersionId}
              isInvalid={Boolean(fieldErrors.memoryVersionId)}
              onChange={(e) => patch({ memoryVersionId: e.target.value })}
            >
              <option value="">Choose a published version…</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version_number} · {shortHash(v.configuration_sha256)} · immutable
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {fieldErrors.memoryVersionId}
            </Form.Control.Feedback>
          </Form.Group>
          {selectedVersion ? (
            <dl className="row mt-3 mb-0 small">
              <dt className="col-sm-4">Change summary</dt>
              <dd className="col-sm-8">{selectedVersion.change_summary || '—'}</dd>
              <dt className="col-sm-4">Skeleton template</dt>
              <dd className="col-sm-8 mono">
                {selectedVersion.configuration?.skeleton_recipe?.template || '—'}
              </dd>
              <dt className="col-sm-4">Rig template</dt>
              <dd className="col-sm-8 mono">
                {selectedVersion.configuration?.rig_recipe?.template || '—'}
              </dd>
              <dt className="col-sm-4">Published</dt>
              <dd className="col-sm-8">
                {selectedVersion.published_at
                  ? new Date(selectedVersion.published_at).toLocaleString()
                  : '—'}
              </dd>
            </dl>
          ) : null}
          {selectedVersion ? (
            <p className="mt-3 mb-0">
              <Link
                to={`/console/projects/${projectId}/rigging/characters/${form.characterId}/versions/${selectedVersion.id}`}
              >
                View Version
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}

      {step.id === 'source' ? (
        <div className="product-card" data-testid="step-source">
          <h3 className="h6">Source revision and scene</h3>
          <Form.Group className="mb-3">
            <Form.Label>Immutable source revision</Form.Label>
            <Form.Select
              value={form.revisionId}
              isInvalid={Boolean(fieldErrors.revisionId)}
              onChange={(e) => patch({ revisionId: e.target.value })}
            >
              <option value="">Choose a revision…</option>
              {revisions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.source_type === 'perforce'
                    ? `Perforce · CL ${r.source_resolved_changelist || 'unresolved'}`
                    : `Uploaded ZIP · ${shortHash(r.content_sha256)}`}{' '}
                  · {r.status}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{fieldErrors.revisionId}</Form.Control.Feedback>
          </Form.Group>
          {selectedRevision?.source_type === 'perforce' &&
          !selectedRevision.source_resolved_changelist ? (
            <Alert variant="warning">
              This Perforce revision has no resolved submitted changelist and cannot be used.
            </Alert>
          ) : null}
          <Form.Group>
            <Form.Label>Source Maya scene (project-relative)</Form.Label>
            <Form.Select
              value={form.sourceScenePath}
              isInvalid={Boolean(fieldErrors.sourceScenePath)}
              onChange={(e) => patch({ sourceScenePath: e.target.value })}
            >
              <option value="">Choose a scene…</option>
              {scenes.map((s) => (
                <option key={s.path} value={s.path}>
                  {s.path}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {fieldErrors.sourceScenePath}
            </Form.Control.Feedback>
            <Form.Text>
              Absolute, UNC, drive-letter, and traversal paths are rejected. Only .ma / .mb scenes
              are supported.
            </Form.Text>
          </Form.Group>
        </div>
      ) : null}

      {step.id === 'operation' ? (
        <div data-testid="step-operation">
          <h3 className="h6">Rigging operation</h3>
          {fieldErrors.operation ? (
            <div className="error-state mb-2">{fieldErrors.operation}</div>
          ) : null}
          {RIGGING_OPERATIONS.map((op) => (
            <RiggingOperationCard
              key={op.id}
              operation={op}
              selected={form.operation === op.id}
              onSelect={(id) => patch({ operation: id })}
            />
          ))}
        </div>
      ) : null}

      {step.id === 'settings' ? (
        <div className="product-card" data-testid="step-settings">
          <h3 className="h6">Operation settings</h3>
          <Form.Group className="mb-3">
            <Form.Label>Job display name (optional)</Form.Label>
            <Form.Control
              value={form.displayName}
              onChange={(e) => patch({ displayName: e.target.value })}
              maxLength={120}
            />
          </Form.Group>
          <Form.Check
            className="mb-2"
            type="checkbox"
            label="Produce preview artifacts when supported"
            checked={form.producePreview}
            onChange={(e) => patch({ producePreview: e.target.checked })}
          />
          <Form.Check
            className="mb-3"
            type="checkbox"
            label="Preserve intermediate artifacts"
            checked={form.preserveIntermediateArtifacts}
            onChange={(e) => patch({ preserveIntermediateArtifacts: e.target.checked })}
          />
          {form.operation === 'maya_engine_export' ? (
            <Form.Group className="mb-3">
              <Form.Label>Export profile</Form.Label>
              <Form.Select
                value={form.exportProfileKey}
                isInvalid={Boolean(fieldErrors.exportProfileKey)}
                onChange={(e) => patch({ exportProfileKey: e.target.value })}
              >
                <option value="">Select export profile…</option>
                {Object.keys(exportProfiles).map((key) => (
                  <option key={key} value={key}>
                    {key} · {exportProfiles[key]?.target}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.exportProfileKey}
              </Form.Control.Feedback>
            </Form.Group>
          ) : null}
          <Alert variant="secondary" className="mb-0">
            Project policy values (units, axes, directories, naming) come from the pinned published
            Project Rig Memory and cannot be edited here.{' '}
            <Link to={`/console/projects/${projectId}/rigging/project-memory`}>
              Edit via a new Project Memory draft
            </Link>
            .
          </Alert>
          {selectedVersion ? (
            <dl className="row mt-3 mb-0 small">
              <dt className="col-sm-4">Skeleton template</dt>
              <dd className="col-sm-8 mono">
                {selectedVersion.configuration?.skeleton_recipe?.template || '—'}
              </dd>
              <dt className="col-sm-4">Rig template</dt>
              <dd className="col-sm-8 mono">
                {selectedVersion.configuration?.rig_recipe?.template || '—'}
              </dd>
              <dt className="col-sm-4">Skinning model</dt>
              <dd className="col-sm-8 mono">
                {selectedVersion.configuration?.skinning_profile?.model || '—'}
              </dd>
            </dl>
          ) : null}
        </div>
      ) : null}

      {step.id === 'review' ? (
        <div className="product-card" data-testid="step-review">
          <h3 className="h6">Review</h3>
          {previewLoading ? (
            <div className="mb-3">
              <Spinner animation="border" size="sm" /> Preparing preview…
            </div>
          ) : null}
          <section className="mb-3">
            <h4 className="h6">Job</h4>
            <p className="mb-0">
              {form.displayName || '(untitled)'} · {operationLabel(form.operation)} · Maya
            </p>
          </section>
          <section className="mb-3">
            <h4 className="h6">Character</h4>
            <p className="mb-0">
              {selectedCharacter?.display_name} ({selectedCharacter?.slug}) ·{' '}
              {selectedCharacter?.character_type}
            </p>
          </section>
          <section className="mb-3">
            <h4 className="h6">Project Rig Memory</h4>
            <p className="mb-1">
              Character memory v{selectedVersion?.version_number} ·{' '}
              {shortHash(selectedVersion?.configuration_sha256)}
            </p>
            <p className="small text-muted mb-0">
              This job will use immutable published configuration. Future Rigging Bible changes will
              not affect it.
            </p>
          </section>
          <section className="mb-3">
            <h4 className="h6">Source</h4>
            <p className="mb-0 mono">{form.sourceScenePath}</p>
            <p className="small text-muted mb-0">
              {selectedRevision?.source_type === 'perforce'
                ? `Changelist ${selectedRevision.source_resolved_changelist}`
                : `Uploaded revision · SHA-256 ${shortHash(selectedRevision?.content_sha256)}`}
            </p>
          </section>
          <section className="mb-3">
            <h4 className="h6">Runtime Requirements</h4>
            <RiggingRuntimeRequirements requirements={preview?.runtime_requirements} />
          </section>
          <section className="mb-3">
            <h4 className="h6">Execution Package</h4>
            <RiggingExecutionPackageSummary preview={preview?.execution_package_preview} />
          </section>
          <section>
            <h4 className="h6">Availability and blockers</h4>
            {blockers.length === 0 && warnings.length === 0 ? (
              <p className="text-muted">No blockers. Warnings may still appear from the server.</p>
            ) : null}
            {blockers.map((b) => (
              <Alert key={b.code} variant="danger" className="py-2">
                {b.message}
              </Alert>
            ))}
            {warnings.map((w) => (
              <Alert key={w.code} variant="warning" className="py-2">
                {w.message}
              </Alert>
            ))}
          </section>
        </div>
      ) : null}

      <div className="d-flex justify-content-between mt-3">
        <Button variant="outline-secondary" onClick={goBack} disabled={stepIndex === 0}>
          Back
        </Button>
        {step.id !== 'review' ? (
          <Button onClick={goNext}>Continue</Button>
        ) : (
          <Button
            onClick={submit}
            disabled={
              submitting ||
              !canCreate ||
              (preview && !preview.valid) ||
              selectedOperation?.availability().available === false
            }
          >
            {submitting ? 'Creating…' : 'Create Rigging Job'}
          </Button>
        )}
      </div>
      {!canCreate ? (
        <Alert variant="info" className="mt-3 mb-0">
          Submission is disabled. You can walk through the workflow for review, but job creation is
          not enabled for your role or environment.
        </Alert>
      ) : null}
    </div>
  );
}
